import {calculateErrorMessage} from 'common/helpers'

import {scrollToElement} from './walkthroughDom'
import type {DOMCache} from './walkthroughDomCache'

export const QUICK_FIND_TIMEOUT_MS = 300

export function getQuickFindOptions(cache: DOMCache | null) {
  return {
    timeout: QUICK_FIND_TIMEOUT_MS,
    pollInterval: 50,
    verbose: false,
    cache,
  }
}

export function isElementObstructed(el: HTMLElement | null): boolean {
  if (!el) return true
  try {
    const rect = el.getBoundingClientRect()
    const samplePoints: Array<{x: number; y: number}> = []

    samplePoints.push({
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2,
    })

    const insetX = Math.max(2, rect.width * 0.15)
    const insetY = Math.max(2, rect.height * 0.15)
    samplePoints.push({x: rect.left + insetX, y: rect.top + insetY})
    samplePoints.push({x: rect.right - insetX, y: rect.top + insetY})
    samplePoints.push({x: rect.left + insetX, y: rect.bottom - insetY})
    samplePoints.push({x: rect.right - insetX, y: rect.bottom - insetY})

    for (const p of samplePoints) {
      if (
        p.x < 0 ||
        p.y < 0 ||
        p.x > (window.innerWidth || document.documentElement.clientWidth) ||
        p.y > (window.innerHeight || document.documentElement.clientHeight)
      ) {
        continue
      }

      const topEl = document.elementFromPoint(p.x, p.y) as HTMLElement | null
      if (!topEl) continue

      if (topEl === el || el.contains(topEl)) {
        return false
      }

      const inWalkthroughPortal =
        topEl.closest('#walkthrough-overlay-portal-root') ||
        (topEl.id && topEl.id.includes('walkthrough')) ||
        (topEl.className &&
          typeof topEl.className === 'string' &&
          topEl.className.includes('walkthrough'))

      if (inWalkthroughPortal) {
        continue
      }

      const modalLike = topEl.closest(
        '[role="dialog"], .modal, .modal-backdrop, .swal2-container, .offcanvas, .drawer, .kt_modal'
      )
      if (modalLike) return true

      return true
    }

    return false
  } catch (err) {
    calculateErrorMessage(err)
    return true
  }
}

export async function waitForElementUnblocked(
  el: HTMLElement | null,
  timeoutMs: number = 30000
): Promise<boolean> {
  if (!el) return false
  const start = Date.now()
  const interval = 500
  return new Promise((resolve) => {
    const check = () => {
      try {
        if (!isElementObstructed(el)) {
          resolve(true)
          return
        }
        if (Date.now() - start > timeoutMs) {
          resolve(false)
          return
        }
      } catch (e) {
        calculateErrorMessage(e)
        resolve(false)
        return
      }
      setTimeout(check, interval)
    }
    check()
  })
}

export function ensureElementInViewAndScroll(el: HTMLElement | null) {
  if (!el) return
  scrollToElement(el)
}
